import { Control, Input, Node, Output, Socket } from 'rete'
import { BaseComponent } from './base_component'
import { NodeData, WorkerInputs, WorkerOutputs } from 'rete/types/core/data'
import { ProjectProperties } from '.'
import { numberSocket, numericDataSocket } from '../socket_types'
import { DateControl } from '../controls/date'
import { maskFromExtentAndShape } from '../bounding_box'
import { BooleanTileGrid, NumericTileGrid } from '../tile_grid'
import { createXYZ } from 'ol/tilegrid'
import { retrieveWFSData } from '../model_retrieval'
import { Geometry, Point } from 'ol/geom'
import { Feature } from 'ol'
import { NumericConstant } from '../numeric_constant'


export class IndSpeciesComponent extends BaseComponent {
    projectProps: ProjectProperties

    constructor(projectProps: ProjectProperties) {
        super('GBIF Indonesia Species Occurrences')
        this.category = 'Indonesia'
        this.projectProps = projectProps
    }

    async builder(node: Node) {

        node.meta.toolTip = 'GBIF Indonesia Species Occurrences. Returns the number of bird species that have been recorded in Indonesia between the given dates. Customizable by resolution (zoom level based grid) and minimum number of occurrences.'
        node.meta.toolTipLink = 'https://www.gbif.org/occurrence/download/0004740-250515123054153/'

        if (!node.data.YearFrom) {
            node.data.YearFrom = new Date(2019, 0, 1).toISOString().split('T')[0]
        }

        if (!node.data.YearTo) {
            node.data.YearTo = new Date(2020, 11, 31).toISOString().split('T')[0]
        }


        node.addInput(new Input('n', 'Minimum number of occurrences (default: 1)', numberSocket))
        node.addInput(new Input('zoom', 'Resolution (zoom level) (default: 13)', numberSocket))

        node.addControl(
            new DateControl(this.editor, 'YearFrom')
        )
        node.addControl(
            new DateControl(this.editor, 'YearTo')
        )

        node.addOutput(new Output('species', 'Unique Species', numericDataSocket))
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {
        let editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) { return }

        const zoom = inputs['zoom'].length > 0 ? (inputs['zoom'][0] as NumericConstant).value : 13
        const n = inputs['n'].length > 0 ? (inputs['n'][0] as NumericConstant).value : 1

        const mask = await maskFromExtentAndShape(this.projectProps)
        const points = await retrieveWFSData('ind:species_points', this.projectProps)
        const pointsInRange: Feature<Geometry>[] = []
        const from = new Date(node.data.YearFrom as string)
        const to = new Date(node.data.YearTo as string)

        for (const point of points) {
            const day = point.get('day')
            const month = point.get('month')
            const year = point.get('year')
            const date = new Date(year, month, day)

            if (date >= from && date <= to && point.get('class') === 'Aves') {
                pointsInRange.push(point)
            }
        }

        const speciesMap: { [species: string]: Feature<Geometry>[] } = {};
        for (const point of pointsInRange) {
            const speciesName = point.get('species');
            if (!speciesMap[speciesName]) {
                speciesMap[speciesName] = [];
            }
            speciesMap[speciesName].push(point);
        }

        const tileGrid = createXYZ()
        const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, zoom)

        const n_out = new NumericTileGrid(
            zoom,
            outputTileRange.minX,
            outputTileRange.minY,
            outputTileRange.getWidth(),
            outputTileRange.getHeight()
        )

        for (const species in speciesMap) {
            const speciesPoints = speciesMap[species]
            const speciesTileGrid = new NumericTileGrid(
                zoom,
                outputTileRange.minX,
                outputTileRange.minY,
                outputTileRange.getWidth(),
                outputTileRange.getHeight()
            )
            for (const point of speciesPoints) {
                const geom = point.getGeometry()
                if (geom instanceof Point) {
                    const point = new Point(geom.getCoordinates())
                    const featureTileRange = tileGrid.getTileRangeForExtentAndZ(
                        point.getExtent(),
                        zoom
                    )
                    const v = isNaN(speciesTileGrid.get(featureTileRange.maxX, featureTileRange.minY)) ? 1 : speciesTileGrid.get(featureTileRange.maxX, featureTileRange.minY) + 1
                    speciesTileGrid.set(featureTileRange.maxX, featureTileRange.minY, v)
                }
            }
            n_out.iterate((x, y, value) => {
                if (speciesTileGrid.get(x, y, zoom) >= n) {
                    const v = isNaN(n_out.get(x, y)) ? 1 : n_out.get(x, y) + 1
                    n_out.set(x, y, v)
                }
            })
        }

        const rescaleOutputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, this.projectProps.zoom)
        const rescaledTileGrid = new NumericTileGrid(
            this.projectProps.zoom,
            rescaleOutputTileRange.minX,
            rescaleOutputTileRange.minY,
            rescaleOutputTileRange.getWidth(),
            rescaleOutputTileRange.getHeight()
        )

        rescaledTileGrid.iterate((x, y, value) => {
            if (n_out.get(x, y, this.projectProps.zoom) && mask.get(x, y)) {
                rescaledTileGrid.set(x, y, n_out.get(x, y, this.projectProps.zoom))
            }
        })

        outputs['species'] = rescaledTileGrid

    }
}